import React, { useState } from 'react';
import { Dice1 as Dice, Copy, Check, RefreshCw } from 'lucide-react';
import { SEO } from '../components/SEO';

interface RandomNumberOptions {
  min: string;
  max: string;
  count: number;
  allowDuplicates: boolean;
  sortResults: boolean;
  includeDecimals: boolean;
  decimalPlaces: number;
}

function GeneratorSlucajnihBrojeva() {
  const [numbers, setNumbers] = useState<number[]>([]);
  const [copied, setCopied] = useState<boolean>(false);
  const [options, setOptions] = useState<RandomNumberOptions>({
    min: '1',
    max: '100',
    count: 1,
    allowDuplicates: true,
    sortResults: false,
    includeDecimals: false,
    decimalPlaces: 2
  });

  const generateRandomNumbers = () => {
    const min = options.includeDecimals 
      ? parseFloat(options.min) 
      : parseInt(options.min);
    const max = options.includeDecimals 
      ? parseFloat(options.max) 
      : parseInt(options.max);
    
    if (isNaN(min) || isNaN(max)) {
      return;
    }

    if (min >= max) {
      alert('Minimalna vrijednost mora biti manja od maksimalne!');
      return;
    }

    const maxPossibleNumbers = options.includeDecimals 
      ? Infinity 
      : max - min + 1;

    if (!options.allowDuplicates && options.count > maxPossibleNumbers) {
      alert(`Možete generirati najviše ${maxPossibleNumbers} jedinstvenih brojeva u ovom rasponu.`);
      return;
    }

    const result: number[] = [];
    const usedNumbers = new Set<number>();

    for (let i = 0; i < options.count; i++) {
      let randomNumber: number;
      
      if (options.includeDecimals) {
        // Generate random decimal number
        randomNumber = min + Math.random() * (max - min);
        // Round to specified decimal places
        randomNumber = parseFloat(randomNumber.toFixed(options.decimalPlaces));
      } else {
        // Generate random integer
        randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
      }

      if (!options.allowDuplicates && usedNumbers.has(randomNumber)) {
        // Try again if we need unique numbers
        i--;
        continue;
      }

      result.push(randomNumber);
      usedNumbers.add(randomNumber);
    }

    if (options.sortResults) {
      result.sort((a, b) => a - b);
    }

    setNumbers(result);
    setCopied(false);
  };

  const copyToClipboard = () => {
    if (numbers.length > 0) {
      navigator.clipboard.writeText(numbers.join(', '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOptionChange = (option: keyof RandomNumberOptions, value: string | number | boolean) => {
    setOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Generator Slučajnih Brojeva",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online generator slučajnih brojeva. Generirajte slučajne brojeve u željenom rasponu s različitim opcijama prilagodbe.",
    "url": "https://online-kalkulator.com/generator-slucajnih-brojeva",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Generator Slučajnih Brojeva | Potpuno Besplatno"
        description="Generirajte slučajne brojeve online. Besplatni generator s opcijama prilagodbe raspona, količine i formata brojeva."
        canonicalUrl="/generator-slucajnih-brojeva"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Dice className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Generator Slučajnih Brojeva</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Generator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Range Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimalna vrijednost
                  </label>
                  <input
                    type="text"
                    value={options.min}
                    onChange={(e) => handleOptionChange('min', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maksimalna vrijednost
                  </label>
                  <input
                    type="text"
                    value={options.max}
                    onChange={(e) => handleOptionChange('max', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 100"
                  />
                </div>
              </div>

              {/* Count and Format Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Broj slučajnih brojeva
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={options.count}
                    onChange={(e) => handleOptionChange('count', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={options.allowDuplicates}
                      onChange={(e) => handleOptionChange('allowDuplicates', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Dozvoli ponavljanje brojeva</span>
                  </label>
                </div>
              </div>

              {/* Additional Options */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={options.sortResults}
                      onChange={(e) => handleOptionChange('sortResults', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Sortiraj rezultate</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={options.includeDecimals}
                      onChange={(e) => handleOptionChange('includeDecimals', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Uključi decimalne brojeve</span>
                  </label>
                </div>

                {options.includeDecimals && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Broj decimalnih mjesta
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={options.decimalPlaces}
                      onChange={(e) => handleOptionChange('decimalPlaces', parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <button
                onClick={generateRandomNumbers}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Generiraj Slučajne Brojeve
              </button>

              {/* Results */}
              {numbers.length > 0 && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-700">Rezultati:</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={generateRandomNumbers}
                        className="p-2 text-gray-500 hover:text-blue-600 transition"
                        title="Generiraj nove brojeve"
                      >
                        <RefreshCw className="w-5 h-5" />
                      </button>
                      <button
                        onClick={copyToClipboard}
                        className="p-2 text-gray-500 hover:text-blue-600 transition"
                        title="Kopiraj u međuspremnik"
                      >
                        {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="max-h-40 overflow-y-auto">
                      <p className="font-mono text-gray-800 break-all">
                        {numbers.join(', ')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Generatoru Slučajnih Brojeva</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator slučajnih brojeva je alat koji generira nasumične brojeve prema vašim
                  specifikacijama. Koristi kriptografski siguran generator slučajnih brojeva za
                  stvaranje nepredvidivih i ravnomjerno distribuiranih brojeva.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Radi</h2>
                <p className="text-gray-600 mb-4">
                  Generator koristi JavaScript funkciju <code>Math.random()</code> za generiranje
                  slučajnih brojeva. Ova funkcija vraća pseudo-slučajni broj između 0 i 1, koji se
                  zatim skalira na željeni raspon.
                </p>
                <p className="text-gray-600">
                  Za generiranje cijelih brojeva, rezultat se zaokružuje na najbliži cijeli broj.
                  Za generiranje decimalnih brojeva, rezultat se zaokružuje na željeni broj
                  decimalnih mjesta.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Postavite minimalnu i maksimalnu vrijednost za raspon brojeva</li>
                  <li>Odredite koliko slučajnih brojeva želite generirati</li>
                  <li>Odaberite dodatne opcije:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Dozvoli ponavljanje brojeva (za više brojeva)</li>
                      <li>- Sortiraj rezultate (od najmanjeg prema najvećem)</li>
                      <li>- Uključi decimalne brojeve (za preciznije vrijednosti)</li>
                      <li>- Postavi broj decimalnih mjesta (ako su uključeni decimalni brojevi)</li>
                    </ul>
                  </li>
                  <li>Kliknite "Generiraj Slučajne Brojeve" za stvaranje brojeva</li>
                  <li>Kopirajte rezultate u međuspremnik klikom na ikonu kopiranja</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjene Slučajnih Brojeva</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Igre na sreću i lutrije</li>
                  <li>Nasumični odabir dobitnika u nagradnim igrama</li>
                  <li>Statistička analiza i simulacije</li>
                  <li>Kriptografija i sigurnost</li>
                  <li>Znanstvena istraživanja</li>
                  <li>Generiranje testnih podataka</li>
                  <li>Nasumični odabir iz popisa</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Generator ima ograničenje od 1000 brojeva po generiranju</li>
                  <li>Bez ponavljanja možete generirati samo onoliko brojeva koliko ih ima u rasponu</li>
                  <li>Za velike raspone s decimalnim brojevima, vjerojatnost ponavljanja je mala</li>
                  <li>Rezultati su prikazani odvojeni zarezima za lakše kopiranje</li>
                  <li>Generirani brojevi su pseudo-slučajni i prikladni za većinu primjena</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjeri Korištenja</h2>
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Bacanje kocke</h3>
                    <p className="text-gray-600">Postavite raspon od 1 do 6 i generirajte jedan broj.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Loto brojevi</h3>
                    <p className="text-gray-600">Postavite raspon od 1 do 39, generirajte 7 brojeva bez ponavljanja i sortirajte rezultate.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-1">Nasumične cijene</h3>
                    <p className="text-gray-600">Uključite decimalne brojeve, postavite raspon od 10 do 100 i odaberite 2 decimalna mjesta.</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default GeneratorSlucajnihBrojeva;